import { memo, useState } from 'react'

import { Box, AppBar, Toolbar, Divider, Avatar, IconButton, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { Popover } from '@shared/components/MaterialUIEnhancements/Popover'
import { User as UserPanel } from 'domains/_Navigation'
import { AppsPanel } from 'domains/_Navigation/AppsPanel'
import { Notifications } from 'domains/_Navigation/Notifications'
import { Panel as SettingsPanel } from 'domains/_Navigation/Settings'
import { useUserInfo } from 'hooks/useUserInfo'
import { getAvatarColor, getContrastText, getInitials } from 'utils/avatar'

const NavigationRaw = () => {
  const { t } = useTranslation()
  const { data: userInfo } = useUserInfo()

  const [userAnchorEl, setUserAnchorEl] = useState<HTMLButtonElement | undefined>()
  const [settingsOpen, setSettingsOpen] = useState(false)

  const displayName = userInfo?.name || [userInfo?.givenName, userInfo?.familyName].filter(Boolean).join(' ') || null

  return (
    <>
      <AppBar
        sx={(theme) => ({ [theme.breakpoints.up('sm')]: { pr: 2 }, width: 320, left: 0, bgcolor: 'background.paper' })}
        position="relative"
      >
        <Toolbar sx={{ height: 76 }} disableGutters>
          <Box sx={{ ml: 1 }}>
            <AppsPanel />
          </Box>

          <Box sx={{ ml: 'auto', mr: 1, display: 'flex', alignItems: 'center' }} component="div">
            <Notifications />
            <Tooltip title={t('user')} placement="bottom" arrow>
              <IconButton
                sx={[!!userAnchorEl && { color: 'info.main' }]}
                onClick={(e) => setUserAnchorEl(e.currentTarget)}
                size="medium"
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: 10,
                    bgcolor: getAvatarColor(displayName),
                    color: `${getContrastText(getAvatarColor(displayName))} !important`,
                  }}
                  alt={displayName || t('profile')}
                  src={userInfo?.picture || undefined}
                >
                  {!userInfo?.picture && getInitials(displayName, userInfo?.givenName, userInfo?.familyName)}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Popover
              open={!!userAnchorEl}
              onClose={() => setUserAnchorEl(undefined)}
              anchorEl={userAnchorEl}
              slotProps={{ paper: { sx: { minWidth: 320 }, variant: 'outlined', elevation: 0 } }}
            >
              <UserPanel onOpenSettings={() => setSettingsOpen(true)} />
            </Popover>
            <Dialog
              open={!!settingsOpen}
              onClose={() => setSettingsOpen(false)}
              slotProps={{ paper: { sx: { minWidth: 720 } } }}
            >
              <SettingsPanel />
            </Dialog>
          </Box>
        </Toolbar>
      </AppBar>
      <Divider sx={{ borderWidth: 2 }} />
    </>
  )
}

export const Navigation = memo(NavigationRaw)
